/** Decides WHICH message a coach sees after a store (App Store / Google Play)
 *  coach-tier purchase or Change Plan. The rule is here rather than in the two
 *  screens so it can be unit-tested without bootstrapping Angular, the same way
 *  the personal Subscribe page's rule lives in
 *  [purchase-outcome.ts](../pages/subscribe/purchase-outcome.ts).
 *
 *  Each screen still draws its own dialog. The become-coach purchase and the
 *  Change Plan page word their messages differently, and Change Plan has no
 *  dedicated message for the two coach-only refusals.
 *
 *  It does NOT call `decidePurchaseOutcomeMessage`, though the shared cases agree
 *  with it. The personal rule asks `success` first, and the coach screens have
 *  always asked every error code first. Keeping the coach order as it was is
 *  what makes this a move with no change in what a coach sees.
 */

import { STORE_VALIDATE_ACCOUNT_MISMATCH, STORE_VALIDATE_REQUEST_REFUSED } from '@cf-shared/store-validate-errors';

/** Which of the coach screens' messages a store coach-tier result should produce. */
export enum CoachTierOutcomeMessage {
  /** Show nothing: the coach backed out of the store sheet. */
  None,
  /** It worked. Each screen shows its own success message. */
  Succeeded,
  /** The store subscription belongs to a different app account. The dialog
   *  names that account when the server told us which. */
  AlreadyLinked,
  /** The store's own record says a different one of our accounts started this
   *  purchase, and no account we can name holds it (#5384). */
  OtherAccount,
  /** The store refused our confirmation request itself (#6239), the same way
   *  for that token every time (#6243). Point the coach at support. */
  StoreRefused,
  /** The coach already holds this tier. The purchase screen points them at
   *  Change Plan. */
  AlreadyOwned,
  /** The server refused because a live personal membership on ANOTHER payment
   *  channel would be double-billed (the single-provider policy, see
   *  design-docs/coach-subscription-coordination.md §6). */
  CrossProviderConflict,
  /** The store took the money and our confirmation never got an answer. Say the
   *  plan MAY still activate. Never say it failed. */
  ConfirmationPending,
  /** It genuinely did not happen, or the store settled it as not valid. */
  Failed,
}

/** The parts of `CoachTierActionResult` this rule reads. Declared here as plain
 *  fields rather than imported, so this module stays free of the Angular
 *  service that owns that interface and the test needs no dependency
 *  injection. */
export interface CoachTierOutcome {
  success: boolean;
  canceled?: boolean;
  error?: string;
  /** Set only where the store is known to have completed, and charged for, the
   *  purchase or tier change BEFORE the failure recorded in `error`. */
  confirmationFailedAfterPurchase?: boolean;
}

/** Picks the coach message for a store result: cancelled first, then the
 *  refusal codes, then charged-but-unconfirmed, then success or failure. */
export function decideCoachTierOutcomeMessage(outcome: CoachTierOutcome): CoachTierOutcomeMessage {
  // if the coach backed out, there is nothing to say
  if (outcome.canceled) return CoachTierOutcomeMessage.None;

  // if the store or the server gave a definite refusal, name it. These win over
  // the confirmation-pending message, because the answer they carry is final.
  if (outcome.error === 'already_linked') return CoachTierOutcomeMessage.AlreadyLinked;
  if (outcome.error === STORE_VALIDATE_ACCOUNT_MISMATCH) return CoachTierOutcomeMessage.OtherAccount;
  if (outcome.error === STORE_VALIDATE_REQUEST_REFUSED) return CoachTierOutcomeMessage.StoreRefused;
  if (outcome.error === 'already_owned') return CoachTierOutcomeMessage.AlreadyOwned;
  if (outcome.error === 'cross_provider_conflict') return CoachTierOutcomeMessage.CrossProviderConflict;

  // if the coach was charged and our confirmation never got an answer, do not
  // say it failed. The flag is the whole condition on purpose: matching on the
  // error text instead would promise a plan to somebody who was never charged.
  if (outcome.confirmationFailedAfterPurchase) return CoachTierOutcomeMessage.ConfirmationPending;

  if (!outcome.success) return CoachTierOutcomeMessage.Failed;
  return CoachTierOutcomeMessage.Succeeded;
}
